<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :model="query" inline class="-mb-15px">
      <el-form-item label="酒店">
        <el-select v-model="query.shopId" clearable filterable class="!w-240px">
          <el-option
            v-for="item in hotels"
            :key="item.id"
            :label="`${item.name}（${item.settleCycle}）`"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="批次状态">
        <el-select v-model="query.status" clearable class="!w-150px">
          <el-option label="成功" value="SUCCESS" />
          <el-option label="失败" value="FAILED" />
          <el-option label="处理中" value="PROCESSING" />
        </el-select>
      </el-form-item>
      <el-form-item label="对账状态">
        <el-select v-model="query.reconciliationStatus" clearable class="!w-160px">
          <el-option label="一致" value="MATCHED" />
          <el-option label="差异" value="MISMATCH" />
          <el-option label="待处理" value="PENDING" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="load"
          ><Icon icon="ep:search" class="mr-5px" />查询</el-button
        >
        <el-button @click="reset"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
        <el-button
          v-hasPermi="['hotel:platform:settlement:execute']"
          type="success"
          :disabled="!query.shopId"
          @click="execute"
          >人工结算</el-button
        >
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="批次号由租户、酒店、周期和截止时间确定；重复执行不会重复转账。对账差额为钱包待结算余额减未批次明细净额。"
    />
    <el-empty
      v-if="!loading && rows.length === 0"
      description="暂无结算批次；支付结算成功后会自动生成，或选择酒店执行人工结算"
    />
    <el-table v-else v-loading="loading" :data="rows">
      <el-table-column prop="batchNo" label="批次号" min-width="270" />
      <el-table-column label="酒店" min-width="170">
        <template #default="{ row }">{{ hotelName(row.shopId) }}</template>
      </el-table-column>
      <el-table-column prop="settleCycle" label="周期" width="85" />
      <el-table-column prop="cutoffTime" label="截止时间" width="175" />
      <el-table-column prop="detailCount" label="明细" width="70" />
      <el-table-column label="支付" width="110"
        ><template #default="{ row }">¥{{ money(row.grossAmount) }}</template></el-table-column
      >
      <el-table-column label="退款" width="110"
        ><template #default="{ row }">¥{{ money(row.refundAmount) }}</template></el-table-column
      >
      <el-table-column label="商户结算" width="120"
        ><template #default="{ row }">¥{{ money(row.merchantAmount) }}</template></el-table-column
      >
      <el-table-column label="状态" width="90">
        <template #default="{ row }"
          ><el-tag :type="row.status === 'SUCCESS' ? 'success' : 'danger'">{{
            row.status
          }}</el-tag></template
        >
      </el-table-column>
      <el-table-column label="对账" width="100">
        <template #default="{ row }"
          ><el-tag :type="row.reconciliationStatus === 'MATCHED' ? 'success' : 'danger'">{{
            row.reconciliationStatus
          }}</el-tag></template
        >
      </el-table-column>
      <el-table-column label="差额" width="110"
        ><template #default="{ row }">¥{{ money(row.differenceAmount) }}</template></el-table-column
      >
      <el-table-column prop="errorMessage" label="异常说明" min-width="200" />
      <el-table-column label="操作" width="90" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="row.status === 'FAILED'"
            v-hasPermi="['hotel:platform:settlement:execute']"
            link
            type="primary"
            @click="retry(row)"
            >重试</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import * as SettlementApi from '@/api/hotel/settlement'

defineOptions({ name: 'HotelPlatformSettlement' })

const message = useMessage()
const loading = ref(false)
const hotels = ref<SettlementApi.HotelPlatformShopOption[]>([])
const rows = ref<any[]>([])
const total = ref(0)
const query = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  shopId: undefined,
  status: undefined,
  reconciliationStatus: undefined
})

const money = (value: any) => Number(value || 0).toFixed(2)
const hotelName = (shopId: string | number) =>
  hotels.value.find((item) => String(item.id) === String(shopId))?.name || `酒店#${shopId}`

const load = async () => {
  loading.value = true
  try {
    const data = await SettlementApi.getSettlementBatchPage(query)
    rows.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const reset = () => {
  Object.assign(query, {
    pageNo: 1,
    shopId: undefined,
    status: undefined,
    reconciliationStatus: undefined
  })
  load()
}

const execute = async () => {
  await message.confirm('确认将该酒店截止当前时间的待结算资金转为可提现吗？')
  const result: any = await SettlementApi.executeSettlement(
    query.shopId,
    dayjs().format('YYYY-MM-DD HH:mm:ss')
  )
  if (result?.status !== 'SUCCESS') {
    message.error(result?.errorMessage || '结算批次执行失败，已进入失败队列')
    await load()
    return
  }
  message.success('结算批次已执行，请核对批次对账状态')
  await load()
}

const retry = async (row: any) => {
  await message.confirm('确认重试该失败批次吗？重复批次号不会重复转账。')
  const result: any = await SettlementApi.retrySettlement(row.id)
  if (result?.status !== 'SUCCESS') {
    message.error(result?.errorMessage || '批次重试失败')
    await load()
    return
  }
  message.success('批次已重试')
  await load()
}

onMounted(async () => {
  hotels.value = (await SettlementApi.getHotelOptions()) || []
  await load()
})
</script>
